/**
 * PredictionCard — MA-PREDICTION-ENGINE-010
 *
 * Displays deterministic forward-looking projections from existing analytics data.
 * Shows qualification trajectory, revenue impact, and time-to-threshold warnings.
 *
 * Language rules (MANDATORY):
 *   Use: "Projected", "At current pace", "If unchanged"
 *   Do NOT use: "Will happen", "Expected"
 */

import React, { useState } from 'react';
import {
  TrendingDown, TrendingUp, Minus,
  AlertTriangle, DollarSign, Clock,
  Info, ChevronDown, ChevronUp,
  ShieldAlert,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const DIRECTION_CONFIG = {
  declining: { color: 'red', icon: TrendingDown, label: 'Qualification Declining' },
  improving: { color: 'emerald', icon: TrendingUp, label: 'Qualification Improving' },
  stable: { color: 'slate', icon: Minus, label: 'Qualification Stable' },
};

function formatRate(decimal) {
  return `${(decimal * 100).toFixed(1)}%`;
}

function formatCurrency(amount) {
  const abs = Math.abs(amount);
  if (abs >= 1000) return `${amount < 0 ? '-' : '+'}$${(abs / 1000).toFixed(1)}k`;
  return `${amount < 0 ? '-' : '+'}$${abs.toLocaleString()}`;
}

/**
 * Unavailable state — shown when prediction cannot be generated.
 */
function PredictionUnavailable({ reasons }) {
  const reasonLabels = {
    low_lead_volume: 'At least 20 leads',
    insufficient_completed_outcomes: 'More completed funnel outcomes',
    insufficient_time_window: 'Sufficient activity over time',
    no_previous_period: 'Previous period comparison data',
    invalid_time_window: 'Valid analysis window',
  };

  return (
    <Card className="glass border-white/10 opacity-80" data-testid="prediction-unavailable">
      <CardContent className="py-5 px-4">
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <ShieldAlert className="h-5 w-5 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-300">Forecast not available yet</h3>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed max-w-sm mx-auto">
            MissionAssist needs more funnel performance data before it can
            project future performance with confidence.
          </p>
          {reasons?.length > 0 && (
            <div className="text-left mx-auto max-w-xs">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Required:</p>
              <ul className="space-y-1">
                {reasons.map((r) => (
                  <li key={r} className="text-xs text-slate-400 flex items-start gap-1.5">
                    <span className="text-slate-600 mt-0.5">&#8226;</span>
                    {reasonLabels[r] || r.replace(/_/g, ' ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Main PredictionCard — renders projections, impact, and threshold warnings.
 */
export default function PredictionCard({ prediction }) {
  const [showBasis, setShowBasis] = useState(false);

  if (!prediction) return null;
  if (!prediction.prediction_available) {
    return <PredictionUnavailable reasons={prediction.reasons} />;
  }

  const {
    trend_direction,
    current_rate,
    qualification_projection,
    impact_projection,
    thresholds,
    confidence,
    basis,
  } = prediction;

  const config = DIRECTION_CONFIG[trend_direction] || DIRECTION_CONFIG.stable;
  const TrendIcon = config.icon;

  const isLowConfidence = confidence === 'low';
  const hasThreat = thresholds.days_to_threshold != null && thresholds.days_to_threshold <= 30;

  return (
    <Card
      className="glass border-white/10"
      data-testid="prediction-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendIcon className={`h-5 w-5 text-${config.color}-400`} />
            <span className={`text-sm font-semibold text-${config.color}-300`}>
              {config.label}
            </span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="prediction-info-btn"
                  className="p-1 rounded hover:bg-white/5 text-slate-500 hover:text-slate-300 transition-colors"
                  onClick={() => setShowBasis(!showBasis)}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs bg-slate-800 border-slate-700">
                <p className="font-medium mb-1">Calculation basis:</p>
                <p>{basis?.leads_count?.toLocaleString()} leads over {basis?.period_days}d</p>
                <p>{basis?.delta_pct_points > 0 ? '+' : ''}{basis?.delta_pct_points}% delta</p>
                <p>${basis?.rql_used?.toLocaleString()} per qualified lead ({basis?.rql_confidence})</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Low confidence warning */}
        {isLowConfidence && (
          <div
            data-testid="low-confidence-warning"
            className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/20"
          >
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-300/90 leading-relaxed">
              Low confidence forecast — based on limited data. Projections may change as more data is collected.
            </p>
          </div>
        )}

        {/* Projected trajectory */}
        <div className="space-y-1.5" data-testid="prediction-trajectory">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">At current pace</p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-100 tabular-nums">
              {formatRate(current_rate)}
            </span>
            <span className="text-slate-600">&rarr;</span>
            <span className={`text-lg font-semibold tabular-nums text-${config.color}-400`}>
              {formatRate(qualification_projection.rate_14d)}
            </span>
            <span className="text-xs text-slate-500">in 14d</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span className="tabular-nums">
              7d: {formatRate(qualification_projection.rate_7d)}
            </span>
            <span className="tabular-nums">
              14d: {formatRate(qualification_projection.rate_14d)}
            </span>
          </div>
        </div>

        {/* Revenue impact */}
        {(impact_projection.impact_7d !== 0 || impact_projection.impact_14d !== 0) && (
          <div className="space-y-1.5" data-testid="prediction-impact">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Projected revenue impact</p>
            <div className="flex items-center gap-2">
              <DollarSign className={`h-4 w-4 text-${impact_projection.impact_14d < 0 ? 'red' : 'emerald'}-400`} />
              <span className={`text-lg font-bold tabular-nums text-${impact_projection.impact_14d < 0 ? 'red' : 'emerald'}-400`}>
                {formatCurrency(impact_projection.impact_14d)}/month
              </span>
              <span className="text-[10px] text-slate-500">if unchanged</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <span className="tabular-nums">7d: {formatCurrency(impact_projection.impact_7d)}</span>
              <span className="tabular-nums">14d: {formatCurrency(impact_projection.impact_14d)}</span>
            </div>
          </div>
        )}

        {/* Threshold warning */}
        {hasThreat && (
          <div
            data-testid="prediction-threshold-warning"
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20"
          >
            <Clock className="h-4 w-4 text-red-400 shrink-0" />
            <p className="text-xs text-red-300">
              Risk threshold ({'<'}{(thresholds.risk_threshold * 100).toFixed(0)}%) projected in{' '}
              <span className="font-bold">~{thresholds.days_to_threshold} days</span>
            </p>
          </div>
        )}

        {/* Basis detail (toggle) */}
        {showBasis && basis && (
          <div
            data-testid="prediction-basis"
            className="text-[11px] text-slate-500 px-3 py-2 rounded-md bg-slate-800/50 border border-slate-700/30 space-y-0.5"
          >
            <p>Leads: {basis.leads_count?.toLocaleString()} over {basis.period_days}d</p>
            <p>Delta: {basis.delta_pct_points > 0 ? '+' : ''}{basis.delta_pct_points}% vs prev period</p>
            <p>RQL: ${basis.rql_used?.toLocaleString()} ({basis.rql_confidence} confidence)</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
